/**
 * Simulador domain types — shared by UI, hooks, and api layer.
 *
 * Mirrors the edge function payloads (supabase/functions/simulator/index.ts).
 * Pools-derived persona shape is in `./content/pools.ts`.
 */

// ─── Shared analysis types (re-exported from unified schema) ─────────────────
export type {
  EtapaSlug,
  AnalysisGrade,
  NarrativeItem,
  FlagCritica,
  FlagCode,
  AcaoCorretiva,
  Gargalo,
  ConsultaReportCore,
  MethodStagesUnified,
  StageEval,
  StageEvalConexao,
  StageEvalPerguntasIniciais,
  StageEvalInvestigacaoDor,
  StageEvalDiagnostico,
  StageEvalOferta,
  StageEvalFechamento,
  SimulatorReport,
  ConsultaReport,
} from "@/lib/analysis/types";

// Backward compat alias — remove when no file imports ReportShape directly
export type { SimulatorReport as ReportShape } from "@/lib/analysis/types";

import type { PersonaSnapshot } from "./content/pools";

export type SimulatorDifficulty = "padrao" | "hard";

export type SimulatorStatus =
  | "active"
  | "paused"
  | "ended"
  | "close_won"
  | "close_lost"
  | "abandoned"
  | "expired";

export type SimulatorMessageRole =
  | "patient"
  | "user"
  | "system"
  | "classifier"
  | "hint";

export type CloseVerdict = "accept" | "delay" | "decline";

export interface ProcedurePublic {
  id: string;
  name: string;
  value_cents: number | null;
  duration_minutes: number | null;
  format: string | null;
}

export interface SimulatorMessage {
  seq: number;
  role: SimulatorMessageRole;
  content: string;
  audio_url?: string | null;
  classifier_meta?: Record<string, unknown> | null;
  created_at?: string;
}

export interface RubricSnapshot {
  connection: number;
  discovery: number;
  objection_break: number;
  clarity: number;
  tailoring: number;
  urgency: number;
}

export interface SessionPublic {
  id: string;
  eneatype: number;
  difficulty: SimulatorDifficulty;
  status: SimulatorStatus;
  last_seq: number;
  created_at: string;
  ended_at: string | null;
}

// API response shapes (edge function)

export interface StartResponse {
  session_id: string;
  persona: PersonaSnapshot;
  procedure: ProcedurePublic;
  first_message: { seq: number; content: string; created_at: string };
}

export interface ReplyResponse {
  patient_message: { seq: number; content: string; created_at: string };
  status: SimulatorStatus;
  close_verdict?: CloseVerdict;
  classifier_signals?: {
    dimensions?: RubricSnapshot;
    signals?: Array<{ kind: string; type?: string; seq?: number }>;
    drift_flags?: string[];
  };
  last_seq: number;
}

export interface ResumeResponse {
  session: SessionPublic;
  persona: PersonaSnapshot;
  procedure: ProcedurePublic;
  messages: SimulatorMessage[];
  last_seq: number;
  dimensions: RubricSnapshot | null;
}

export interface EndResponse {
  session_id: string;
  status: SimulatorStatus;
  report_pending: boolean;
}

export interface AchievementRow {
  eneatype: number;
  difficulty: SimulatorDifficulty;
  session_id: string;
  final_score: number;
  unlocked_at: string;
}

export interface AchievementsResponse {
  achievements: AchievementRow[];
  total: number;
  of: 9;
}

export interface HeatmapRow {
  objection_type: string;
  occurrences: number;
  sessions_touched: number;
  last_seen: string;
}

export interface LeaderboardEntry {
  user_id: string;
  display_name: string;
  achievements_count: number;
  last_unlocked_at: string;
  zerou: boolean;
}

export interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  my_rank: number | null;
}

// ─── Método Influência Máxima — 6 Etapas ────────────────────────────────────

export type EtapaSlug =
  | "conexao"
  | "perguntas_iniciais"
  | "investigacao_dor"
  | "diagnostico_clinico"
  | "oferta"
  | "fechamento"
  | "geral";

export type MethodGrade = "aprovado" | "bom" | "treinar" | "recomecar";

export interface MethodStageEval {
  score: number;                  // 0-10
  criterios_atingidos: string[];
  criterios_perdidos: string[];
}

export interface MethodStages {
  conexao:             MethodStageEval;
  perguntas_iniciais:  MethodStageEval;
  investigacao_dor:    MethodStageEval;
  diagnostico_clinico: MethodStageEval;
  oferta:              MethodStageEval;
  fechamento:          MethodStageEval;
}

// Tagged narrative item — text + which stage it belongs to.
export interface NarrativeItem {
  text: string;
  etapa: EtapaSlug;
}

// ─── ReportShape ─────────────────────────────────────────────────────────────

export interface ReportShape {
  session_id: string;
  user_id: string;
  final_score: number;
  dimensions: RubricSnapshot;
  drag_penalty: number;
  hint_penalty: number;
  verdict: CloseVerdict | null;

  // Narrative — accepts both legacy strings and new tagged objects
  strengths:       Array<string | NarrativeItem>;
  improvements:    Array<string | NarrativeItem>;
  recommendations: Array<string | NarrativeItem>;

  key_moments: Array<{
    seq: number;
    type: "positive" | "objection" | "opportunity" | "error" | "close" | "stall";
    label: string;
    excerpt: string;
  }>;
  annotations: Array<{
    seq: number;
    kind:
      | "good"
      | "missed_signal"
      | "weak_close"
      | "drift"
      | "poor_objection_handling"
      | "strong_handling"
      | "missed_stall_recovery"
      | "etapa_pulada";
    comment: string;
    suggested_better?: string;
    severity: "low" | "medium" | "high";
    etapa?: EtapaSlug;
  }>;

  // ─── Método Influência Máxima (v2+) ─────────────────────────────────────
  method_stages?:   MethodStages;
  method_total?:    number;           // 0-60
  method_grade?:    MethodGrade;
  grade?:           string | null;
  etapa_critica?:   string | null;

  flags_criticas?: Array<{
    flag: string;
    detectada: boolean;
    evidencia?: string | null;
    impacto: "alto" | "medio" | "baixo";
  }>;

  gargalo_principal?: {
    etapa: string;
    descricao: string;
    impacto_na_conversao: string;
  } | null;

  palavra_emocional_central?: string | null;

  nivel_dor_maximo?: {
    nivel: 1 | 2 | 3;
    descricao: string;
    evidencia: string;
  } | null;

  pratica?: {
    treinar:              string[];
    foco_proxima_sessao?: string | null;
    simular_novamente?:   string;     // legacy alias
  };

  self_reflection?: {
    como_foi:               string;
    ponto_forte_percebido:  string;
  };

  generated_at: string;
}

// UI-only state machine

export type SimulatorViewState =
  | { kind: "setup" }
  | { kind: "starting"; eneatype: number; difficulty: SimulatorDifficulty }
  | {
      kind: "chat";
      sessionId: string;
      persona: PersonaSnapshot;
      procedure: ProcedurePublic;
      difficulty: SimulatorDifficulty;
      messages: SimulatorMessage[];
      lastSeq: number;
      isPatientTyping: boolean;
    }
  | { kind: "ended"; sessionId: string }
  | { kind: "report"; sessionId: string; report: ReportShape };

export const DIFFICULTY_LABEL: Record<SimulatorDifficulty, string> = {
  padrao: "Padrão",
  hard: "Difícil",
};

export const ENEATYPE_NAMES: Record<number, string> = {
  1: "Perfeccionista",
  2: "Doador",
  3: "Realizador",
  4: "Individualista",
  5: "Investigador",
  6: "Lealista",
  7: "Entusiasta",
  8: "Desafiador",
  9: "Pacificador",
};
